import type { CSSProperties } from 'react';
import { CATEGORY_TINTS } from '@sitzmark/design';

/**
 * The colour a category wears, everywhere it appears.
 *
 * Derived from the id rather than stored, for two reasons. A colour column
 * would be one more thing to choose when creating a category — and §2.2-5 says
 * every input pre-fills its most likely value, which for "what colour?" is
 * "any of them, I do not care". And derived means the tile on the categories
 * screen and the tint on an import row cannot drift apart, because there is
 * nothing to keep in step.
 *
 * Stable across reloads and across machines: the same category is the same
 * colour on every till, which is what makes "the blue ones are jackets" a thing
 * two people can say to each other.
 *
 * Collisions are fine and expected past eight categories. The colour is a
 * scanning aid for a name that is always printed beside it, never the message
 * itself (§2.1) — so two categories sharing a tint costs a glance, not a
 * mistake.
 */
export function categoryTintIndex(categoryId: string): number {
  /*
   * FNV-1a. Any stable hash would do; this one is four lines, has no
   * dependencies, and spreads sequential UUIDv7s — which share a long timestamp
   * prefix — far better than summing char codes does.
   */
  let hash = 0x811c9dc5;
  for (let index = 0; index < categoryId.length; index += 1) {
    hash ^= categoryId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % CATEGORY_TINTS.length;
}

export function categoryTint(categoryId: string): CSSProperties {
  const tint = CATEGORY_TINTS[categoryTintIndex(categoryId)]!;
  return { background: tint.bg, color: tint.fg, borderColor: tint.fg };
}

/** The background alone, for tinting a whole table row behind ordinary text. */
export function categoryRowTint(categoryId: string): CSSProperties {
  return { background: CATEGORY_TINTS[categoryTintIndex(categoryId)]!.bg };
}
